/**
 * « 🚚 Livrer cette commande » — une commande en ligne part en livraison
 * (12/10/2026). Payée en ligne, le livreur n'encaisse rien ; « à régler »
 * (paiement accordé à la remise, 21/10/2026), il encaisse à la porte. Elle
 * entre dans la tournée du jour choisi dès qu'elle est « prête ».
 *
 * Le ticket qui part dans le sac est celui de la préparation (même gabarit) :
 * il s'imprime ici seulement si la commande est DÉJÀ prête — sinon à « Commande
 * prête », avec le montant après pesée. Il imprimait toujours « DEJA PAYE »,
 * total 0, même sur une commande à régler (22/10/2026).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { jourDeLivraisonPropose } from '../../utils/livraisonCaisse';
import { PrinterAdapterFactory, generatePickupTicket } from '../../services/printers';
import type { PrinterConfig } from '../../services/printers';
import AdresseLivraison, { type Adresse } from './AdresseLivraison';

interface Props {
  open: boolean;
  order: {
    id: string; order_number: string; customer_id: string; customer_name: string; customer_phone?: string | null;
    delivery_address_id?: string; delivery_date?: string;
    status?: string; payment_status?: string; total_cents?: number;
    refunded_cents?: number; balance_due_cents?: number;
    lines?: any[];
    metadata?: any;
  };
  onClose: () => void;
  onFait: () => void;
}

type Impression = 'imprime' | 'apres-preparation' | 'sans-imprimante';

export default function LivrerCommandeModal({ open, order, onClose, onFait }: Props) {
  const { tenant } = useTenant();
  const jours: number[] | undefined = (tenant?.config as any)?.ecommerce?.delivery?.days;
  const [adresse, setAdresse] = useState<Adresse | null>(null);
  const [date, setDate] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [colis, setColis] = useState(1);

  useEffect(() => {
    if (!open) return;
    setAdresse(null);
    setDate(order.delivery_date ? order.delivery_date.slice(0, 10) : jourDeLivraisonPropose(jours));
    setEnvoi(false);
    // « Modifier la livraison » : on repart du nombre déjà noté — les tickets
    // « COLIS k/n » sont peut-être déjà collés sur les sacs.
    setColis(Math.max(1, Math.min(12, Number(order.metadata?.colis) || 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order.id]);

  if (!open) return null;

  const payee = order.payment_status === 'paid';
  const montant = `${((Number(order.total_cents) || 0) / 100).toFixed(2).replace('.', ',')} €`;

  /**
   * Ticket du sac (même gabarit que la préparation), sur l'EPSON si la caisse est
   * à côté. Seulement pour une commande déjà préparée : avant, le montant n'est
   * pas encore pesé — le ticket sortira à « Commande prête ». Jamais bloquant.
   */
  const imprimer = async (a: Adresse): Promise<Impression> => {
    if (order.status !== 'ready') return 'apres-preparation';
    try {
      if (!(await PrinterAdapterFactory.checkHardwareBridge())) return 'sans-imprimante';
      const lienClient = await api.lienClientCommande(order.id).catch(() => null);
      const printerConfig = (tenant?.config as any)?.hardware?.printer || {};
      const config: PrinterConfig = { model: printerConfig.type || 'BROWSER_PRINT', connection: 'USB', settings: printerConfig.settings };
      const printer = await PrinterAdapterFactory.createAsync(config);
      await printer.connect();
      await printer.print(generatePickupTicket({
        order_number: order.order_number,
        customer_name: order.customer_name,
        customer_phone: order.customer_phone,
        delivery_option: 'delivery',
        payment_status: order.payment_status,
        colis,
        delivery_date: date,
        delivery_address: { street: a.street, postal_code: a.postal_code, city: a.city },
        total_cents: Number(order.total_cents) || 0,
        refunded_cents: Number(order.refunded_cents) || 0,
        balance_due_cents: Number(order.balance_due_cents) || 0,
        lines: (order.lines || []) as any,
        bacs_rapportes: order.metadata?.bacs?.rapportes ?? 0,
        lienClient,
        storeName: (tenant?.config as any)?.storeInfo?.name || tenant?.name || undefined,
      }));
      await printer.disconnect();
      return 'imprime';
    } catch {
      return 'sans-imprimante';
    }
  };

  const valider = async () => {
    if (!adresse || !date) return;
    setEnvoi(true);
    try {
      await api.patch(`/orders/${order.id}/a-livrer`, { delivery_address_id: adresse.id, delivery_date: date, colis });
      const impression = await imprimer(adresse);
      const suite =
        impression === 'imprime'
          ? ' + ticket de livraison imprimé'
          : impression === 'apres-preparation'
            ? ' — le ticket sortira à « Commande prête »'
            : ' (ticket non imprimé : pas d’imprimante ici — réimprimez-le depuis Préparation)';
      toast.success(`${order.order_number} : livraison prévue${suite}`);
      onFait();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Livraison non enregistrée');
      setEnvoi(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-sky-700 px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">🚚 Livrer {order.order_number}</h2>
            <p className="text-sky-100 text-sm">
              {order.customer_name} —{' '}
              {payee ? 'déjà payée, rien à encaisser' : `à régler à la livraison : ${montant} (Bancontact ou espèces)`}
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-3xl leading-none" aria-label="Fermer">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <AdresseLivraison customerId={order.customer_id} value={adresse} onChange={setAdresse} preferee={order.delivery_address_id} />
          <label className="block"><span className="text-sm font-semibold text-gray-700">Livraison le</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-3 text-lg" /></label>
          <div>
            <span className="text-sm font-semibold text-gray-700">Nombre de colis (sur l’étiquette Zebra et le ticket)</span>
            <div className="mt-1 flex items-center gap-3">
              <button type="button" onClick={() => setColis((c) => Math.max(1, c - 1))} className="w-14 h-14 rounded-xl border-2 border-gray-300 text-2xl font-bold" aria-label="Un colis de moins">−</button>
              <span className="w-16 text-center text-3xl font-bold tabular-nums">{colis}</span>
              <button type="button" onClick={() => setColis((c) => Math.min(12, c + 1))} className="w-14 h-14 rounded-xl border-2 border-gray-300 text-2xl font-bold" aria-label="Un colis de plus">+</button>
            </div>
          </div>
          <p className="text-sm text-gray-600">La commande rejoint la tournée de ce jour dès qu’elle est marquée « prête ».</p>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-3 rounded-xl border">Annuler</button>
          <button onClick={valider} disabled={!adresse || !date || envoi} className="px-6 py-3 rounded-xl bg-sky-700 text-white font-bold disabled:bg-gray-300">
            {envoi ? 'Enregistrement…' : 'Livrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
